// src/components/operationCenter/widgets/TradingViewWidget.tsx
// TradingView advanced chart widget for the dashboard
// Persists user changes (ticker, interval, theme) to local storage via widget config

import React, {
  useEffect,
  useRef,
  memo,
  useCallback,
  useState,
  useMemo,
} from "react";
import { LineChart, Search, X, ChevronDown, Check } from "lucide-react";
import type { WidgetProps, TradingViewConfig } from "@/types/dashboard";
import { useDashboardStore } from "@/stores/dashboardStore";
import { useTokensCatalog } from "@/hooks/useTokensCatalog";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { MetricKeys } from "@/constants/metrics";

// Local storage key prefix for TradingView widget state
const TV_STORAGE_PREFIX = "tradingview_widget_state_";

// Supported exchanges with their symbol format
const EXCHANGES = [
  { id: "BINANCE", name: "Binance", suffix: "USDT" },
  { id: "COINBASE", name: "Coinbase", suffix: "USD" },
  { id: "BYBIT", name: "Bybit", suffix: "USDT" },
  { id: "OKX", name: "OKX", suffix: "USDT" },
] as const;

type ExchangeId = (typeof EXCHANGES)[number]["id"];

// Get saved state from localStorage (fallback if widget config wasn't updated)
const getSavedState = (widgetId: string): Partial<TradingViewConfig> | null => {
  try {
    const saved = localStorage.getItem(`${TV_STORAGE_PREFIX}${widgetId}`);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

// Save state to localStorage
const saveStateToStorage = (
  widgetId: string,
  state: Partial<TradingViewConfig>
) => {
  try {
    const existing = getSavedState(widgetId) || {};
    localStorage.setItem(
      `${TV_STORAGE_PREFIX}${widgetId}`,
      JSON.stringify({ ...existing, ...state, lastUpdated: Date.now() })
    );
  } catch {
    // Ignore storage errors
  }
};

const TradingViewWidget: React.FC<WidgetProps<TradingViewConfig>> = ({
  widget,
}) => {
  const config = widget.config as TradingViewConfig;
  const containerRef = useRef<HTMLDivElement>(null);
  const updateWidgetConfig = useDashboardStore((s) => s.updateWidgetConfig);

  // Get saved state from localStorage as fallback
  const savedState = getSavedState(widget.id);

  // Use saved state as fallback if config doesn't have values
  const ticker = config.ticker ?? savedState?.ticker ?? "BTC";
  const interval = config.interval ?? savedState?.interval ?? "D";
  const theme = config.theme ?? savedState?.theme ?? "dark";
  const exchange = (config.exchange ??
    savedState?.exchange ??
    "BINANCE") as ExchangeId;

  // Fetch token catalog and screener data to get available tickers
  const { data: tokenCatalog } = useTokensCatalog();
  const { data: screenerRows, isLoading: isScreenerLoading } =
    useTokensScreenerData();

  // Filter tokens that have derivatives data (same as OpenInterestWidget)
  const derivativesTickers = useMemo(() => {
    if (!Array.isArray(screenerRows)) return new Set<string>();
    const out = new Set<string>();
    screenerRows.forEach((row) => {
      const ticker = String(row["tokenSymbol"] ?? "").toUpperCase();
      if (!ticker) return;
      const oi = Number(row[MetricKeys.FuturesOI]);
      const frOiw = Number(row[MetricKeys.FuturesFROIW]);
      const frVolw = Number(row[MetricKeys.FuturesFRVolW]);
      if ([oi, frOiw, frVolw].some((v) => Number.isFinite(v))) {
        out.add(ticker);
      }
    });
    return out;
  }, [screenerRows]);

  const availableTokens = useMemo(
    () =>
      (tokenCatalog ?? [])
        .map((token) => token.ticker?.toUpperCase().trim())
        .filter((ticker): ticker is string => Boolean(ticker))
        .filter((ticker) => derivativesTickers.has(ticker))
        .sort(), // Sort alphabetically
    [tokenCatalog, derivativesTickers]
  );

  // Local state for dropdown and search
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isExchangeDropdownOpen, setIsExchangeDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const exchangeDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filtered tokens based on search query
  const filteredTokens = useMemo(() => {
    if (!searchQuery) return availableTokens;
    const query = searchQuery.toLowerCase();
    return availableTokens.filter((t) => t.toLowerCase().includes(query));
  }, [availableTokens, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen && !isExchangeDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
        setSearchQuery("");
      }
      if (
        exchangeDropdownRef.current &&
        !exchangeDropdownRef.current.contains(e.target as Node)
      ) {
        setIsExchangeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen, isExchangeDropdownOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  // Get current exchange config
  const currentExchange =
    EXCHANGES.find((e) => e.id === exchange) ?? EXCHANGES[0];

  // Construct the TradingView symbol based on selected exchange
  const symbol = `${currentExchange.id}:${ticker.toUpperCase()}${
    currentExchange.suffix
  }`;

  // Persist config changes to store (which persists to localStorage via Zustand)
  const saveConfig = useCallback(
    (updates: Partial<TradingViewConfig>) => {
      // Save to widget config (persisted via Zustand)
      updateWidgetConfig(widget.id, updates);
      // Also save to dedicated localStorage as backup
      saveStateToStorage(widget.id, updates);
    },
    [updateWidgetConfig, widget.id]
  );

  // Handle ticker selection
  const handleTickerSelect = useCallback(
    (newTicker: string) => {
      if (newTicker !== ticker) {
        saveConfig({ ticker: newTicker });
      }
      setIsDropdownOpen(false);
      setSearchQuery("");
    },
    [ticker, saveConfig]
  );

  // Handle exchange selection
  const handleExchangeSelect = useCallback(
    (newExchange: ExchangeId) => {
      if (newExchange !== exchange) {
        saveConfig({ exchange: newExchange });
      }
      setIsExchangeDropdownOpen(false);
    },
    [exchange, saveConfig]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Clear any existing content
    container.innerHTML = "";

    // Create widget container div
    const widgetDiv = document.createElement("div");
    widgetDiv.className = "tradingview-widget-container__widget";
    widgetDiv.style.height = "100%";
    widgetDiv.style.width = "100%";
    container.appendChild(widgetDiv);

    // Create and configure script
    const script = document.createElement("script");
    script.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.type = "text/javascript";
    script.async = true;

    // Widget configuration
    const widgetConfig = {
      autosize: true,
      symbol,
      interval,
      timezone: "Etc/UTC",
      theme,
      style: "1", // Candlestick
      locale: "en",
      allow_symbol_change: false, // Disabled - use header controls instead
      calendar: false,
      hide_top_toolbar: false,
      hide_side_toolbar: true,
      hide_legend: false,
      hide_volume: false,
      support_host: "https://www.tradingview.com",
      backgroundColor:
        theme === "dark" ? "rgba(15, 23, 42, 1)" : "rgba(255, 255, 255, 1)",
      gridColor: "rgba(100, 116, 139, 0.1)",
      withdateranges: true,
      details: false,
      hotlist: false,
      save_image: false,
      studies: [],
      container_id: `tradingview-widget-${widget.id}`,
    };

    script.innerHTML = JSON.stringify(widgetConfig);

    // Add error handling
    script.onerror = () => {
      if (container) {
        container.innerHTML = `
          <div class="flex items-center justify-center h-full text-slate-500">
            <div class="text-center">
              <p class="text-sm">Failed to load chart</p>
              <p class="text-xs text-slate-600 mt-1">Please refresh to try again</p>
            </div>
          </div>
        `;
      }
    };

    container.appendChild(script);

    // Cleanup
    return () => {
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [symbol, interval, theme, widget.id]);

  return (
    <div className="h-full flex flex-col">
      {/* Header with enhanced ticker selector */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        <LineChart className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />

        {/* Exchange Selector */}
        <div className="relative" ref={exchangeDropdownRef}>
          <button
            onClick={() => setIsExchangeDropdownOpen(!isExchangeDropdownOpen)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-200 bg-gradient-to-br from-slate-700/80 to-slate-800/80 hover:from-slate-700 hover:to-slate-800 rounded-lg transition-all duration-200 border border-slate-600/50 hover:border-cyan-500/50 shadow-sm"
          >
            <span className="font-semibold">{currentExchange.name}</span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${
                isExchangeDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {isExchangeDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-40 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl z-50 overflow-hidden py-1">
              {EXCHANGES.map((ex) => (
                <div
                  key={ex.id}
                  onClick={() => handleExchangeSelect(ex.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleExchangeSelect(ex.id)
                  }
                  className={
                    "w-full flex items-center justify-between px-3 py-2 text-sm cursor-pointer select-none transition-colors " +
                    (ex.id === exchange
                      ? "bg-cyan-500/20 text-cyan-300"
                      : "text-slate-300 hover:bg-slate-700/50")
                  }
                >
                  <span className="font-medium">{ex.name}</span>
                  {ex.id === exchange && (
                    <Check className="w-4 h-4 text-cyan-400" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Enhanced Dropdown Ticker Selector */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            disabled={isScreenerLoading || availableTokens.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-200 bg-gradient-to-br from-slate-700/80 to-slate-800/80 hover:from-slate-700 hover:to-slate-800 rounded-lg transition-all duration-200 border border-slate-600/50 hover:border-cyan-500/50 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="font-semibold">{ticker}</span>
            <span className="text-slate-400 text-xs">
              /{currentExchange.suffix}
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
              {/* Search Input */}
              <div className="p-2 border-b border-slate-700/50 bg-slate-800/50">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tokens..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm bg-slate-900/50 border border-slate-600 rounded-md text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition-all"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Token List with Smooth Scroll */}
              <div className="max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800/50 scroll-smooth">
                {filteredTokens.length > 0 ? (
                  <div className="py-1">
                    {filteredTokens.map((token, idx) => (
                      <div
                        key={token}
                        onClick={() => handleTickerSelect(token)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleTickerSelect(token)
                        }
                        className={
                          "w-full flex items-center justify-between px-3 py-2 text-sm transition-all duration-150 cursor-pointer select-none " +
                          (token === ticker
                            ? "bg-cyan-500/20 text-cyan-300 font-medium"
                            : "text-slate-300 hover:bg-slate-700/50 hover:text-slate-100")
                        }
                        style={{
                          animationDelay: `${Math.min(idx, 10) * 15}ms`,
                          animationName: "slideIn",
                          animationDuration: "200ms",
                          animationTimingFunction: "ease-out",
                          animationFillMode: "both",
                        }}
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-medium">{token}</span>
                          <span className="text-xs text-slate-500">
                            /{currentExchange.suffix}
                          </span>
                        </span>
                        {token === ticker && (
                          <Check className="w-4 h-4 text-cyan-400" />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-sm text-slate-500">
                    {searchQuery ? "No tokens found" : "Loading tokens..."}
                  </div>
                )}
              </div>

              {/* Footer Info */}
              <div className="px-3 py-1.5 border-t border-slate-700/50 bg-slate-800/50 text-xs text-slate-500">
                {filteredTokens.length}{" "}
                {filteredTokens.length === 1 ? "token" : "tokens"} available
              </div>
            </div>
          )}
        </div>
      </div>

      {/* TradingView Chart */}
      <div
        ref={containerRef}
        className="tradingview-widget-container flex-1 w-full"
        style={{ minHeight: "250px" }}
      />
    </div>
  );
};

export default memo(TradingViewWidget);
